/**
 * 环境变量与中继地址解析。
 *
 * 中继地址三级解析（ARCH T01.7）：
 *   ① `.env` 的 `VITE_RELAY_ADDR`
 *   ② 本地服务 HTTP `/relay-info`（自动发现，拿到真实 PeerID）
 *   ③ 内置默认值 `/ip4/127.0.0.1/tcp/9101/ws`
 *
 * 注：③ 的内置默认地址**不含** `/p2p/<peerId>`；此时对端身份由拨号握手反查回填
 * （见 `src/net/peer.ts#connectRelay`），不依赖地址携带。
 */

import type { RelayInfo } from '@shared/types';
import type { LanAddress } from '@shared/lan-candidates';

/** 再导出 `LanAddress`（定义在 `shared/lan-candidates.ts`，避免 shared→src 反向依赖）。 */
export type { LanAddress };

/** 内置默认中继地址（第 ③ 级）。 */
export const DEFAULT_RELAY_ADDR = '/ip4/127.0.0.1/tcp/9101/ws';

/**
 * 把中继地址补全为「已指明对端」的多地址。
 *
 * 事实更正：js-libp2p **仅在「过滤后无可用地址」时**才抛 `The dial request has no valid addresses`
 * （见 `libp2p/src/connection-manager/dial-queue.ts`）；地址**是否携带** `/p2p/<peerId>` 不影响
 * 能否拨通。携带 PeerID 的意义是**拨号目标明确、省掉一次 identify 反查**，而非「能不能连上」。
 * 对端身份的**最终来源**是拨号握手结果 —— 见 `src/net/peer.ts#connectRelay`（裸地址时从连接反查）。
 * 因此当已知 PeerID 而地址未携带时在此追加（对 ① 级 env 带 PeerID 的场景仍有价值）。
 */
export function withPeerSuffix(addr: string, peerId?: string): string {
  if (!addr) return addr;
  if (addr.includes('/p2p/')) return addr;
  return peerId ? `${addr}/p2p/${peerId}` : addr;
}

/**
 * 本地服务 HTTP 基址。
 * 默认**同源**（空串 → `/relay-info` 等相对路径，经 Vite 反代到 :9200）：
 * 手机访问 `https://<局域网IP>:5173` 时，写死 `127.0.0.1:9200` 会指向手机自身。
 * 需要跨域直连时用 `VITE_SERVER_HTTP` 显式覆盖。
 */
export const SERVER_HTTP_BASE = (import.meta.env?.VITE_SERVER_HTTP as string | undefined) ?? '';

/**
 * 共享链 WS 地址。默认同源 `/chainws`（nginx/Vite WS 反代 → :9200/chain），
 * 手机/桌面同一套地址，无需感知后端端口。`VITE_CHAIN_WS` 可覆盖。
 */
export const CHAIN_WS_URL =
  (import.meta.env?.VITE_CHAIN_WS as string | undefined) ??
  (typeof location !== 'undefined'
    ? `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/chainws`
    : 'ws://127.0.0.1:9200/chain');

/** 链模式：shared = 连服务端共享账本；local = 浏览器内独立账本（降级）。 */
export const CHAIN_MODE: 'shared' | 'local' =
  (import.meta.env?.VITE_CHAIN_MODE as string | undefined) === 'local' ? 'local' : 'shared';

/** `.env` 显式指定的中继地址（第 ① 级，可为空）。 */
export const ENV_RELAY_ADDR = (import.meta.env?.VITE_RELAY_ADDR as string | undefined)?.trim() ?? '';

/** `.env` 显式指定的中继 PeerID（可选，用于跳过发现）。 */
export const ENV_RELAY_PEER_ID = (import.meta.env?.VITE_RELAY_PEER_ID as string | undefined)?.trim() ?? '';

/**
 * 备用中继列表（防封锁链式部署）：`VITE_RELAY_LIST=/ip4/.. /p2p/..,/ip4/.. /p2p/..`
 * 逗号分隔多地址。注入 HybridTransport 后，libp2p dial-queue 会依次尝试——
 * 主中继不可达时自动切换备用中继（无需手动重连）。
 */
export const ENV_RELAY_LIST: string[] =
  (import.meta.env?.VITE_RELAY_LIST as string | undefined)
    ?.split(',')
    .map((s) => s.trim())
    .filter(Boolean) ?? [];

/** localStorage 白名单键（ARCH §8.4.7：只允许这三项）。 */
export const LS_KEYS = {
  theme: 'meshlink.theme',
  lastDid: 'meshlink.lastDid',
  onboarded: 'meshlink.onboarded',
} as const;

/** 空闲自动锁定时长（分钟），默认 15（PRD §6 关键触点）。 */
export const DEFAULT_AUTO_LOCK_MINUTES = 15;

/** 自动锁定时长可选项（分钟）。 */
export const AUTO_LOCK_OPTIONS = [5, 15, 30, 60] as const;

/** 消息分页大小（上拉加载）。 */
export const MESSAGE_PAGE_SIZE = 30;

/** mailbox 轮询间隔（ms）。 */
export const MAILBOX_POLL_INTERVAL_MS = 30_000;

/** 心跳间隔（ms）。 */
export const HEARTBEAT_INTERVAL_MS = 15_000;

/** 内容协议限制：单 blob ≤ 4MB（压缩后图片/短视频/语音均在此内），单帖 ≤ 6 张。 */
export const CONTENT_MAX_BLOB_BYTES = 4 * 1024 * 1024;
export const CONTENT_MAX_IMAGES_PER_POST = 6;

/**
 * 扫码互联默认接入端口（dev=5173 Vite；生产由 `VITE_JOIN_PORT` 覆盖，指向 nginx 反代端口）。
 * 与 `shared/qr-link.ts#JOIN_ACCESS_PORT_DEFAULT` 保持一致。
 */
export const JOIN_ACCESS_PORT = Number(
  (import.meta.env?.VITE_JOIN_PORT as string | undefined) ?? 5173,
);

/**
 * 允许「离线互联」入口显式打开的判定：中继连续未 ready 超过该时长（ms）。
 * （ARCH_OfflineQR_Mesh §4.2 / Q4：中继 8s 未就绪即在 Header 挂提示入口。）
 */
export const ISOLATED_HINT_MS = 8_000;

/** `LanAddress` 结构校验（`/lan-info` 返回体可能被篡改/异常，逐字段收窄）。 */
function isLanAddress(value: unknown): value is LanAddress {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.ip === 'string' &&
    v.ip.length > 0 &&
    (v.family === 4 || v.family === 6) &&
    typeof v.iface === 'string'
  );
}

/**
 * 拉 `/lan-info`（复用 `SERVER_HTTP_BASE` 同源策略，与 `fetchRelayInfo` 同款超时/降级）。
 *
 * 失败（超时 / 非 2xx / 结构非法）**降级为空数组**（对应 F-6：Host 端服务未起 → 空候选，
 * 由上层提示「请先启动本地服务」）。永不抛异常。
 */
export async function fetchLanCandidates(timeoutMs = 2500): Promise<LanAddress[]> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(`${SERVER_HTTP_BASE}/lan-info`, { signal: controller.signal });
    if (!res.ok) return [];
    const data = (await res.json()) as { candidates?: unknown };
    if (!Array.isArray(data.candidates)) return [];
    return (data.candidates as unknown[]).filter(isLanAddress);
  } catch {
    return [];
  } finally {
    clearTimeout(timer);
  }
}

/**
 * 由 LAN 候选组装可供 Guest 拨号的接入点（`'192.168.100.100:5173'`）。
 * @param c 已过滤/排序的候选地址。
 * @param opts.port 接入端口；默认 `JOIN_ACCESS_PORT`。
 */
export function buildJoinHost(c: LanAddress, opts?: { port?: number }): string {
  return `${c.ip}:${opts?.port ?? JOIN_ACCESS_PORT}`;
}

/** 拉取 /relay-info（第 ② 级解析）。失败返回 null，由调用方回落到默认值。 */
export async function fetchRelayInfo(timeoutMs = 2500): Promise<RelayInfo | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(`${SERVER_HTTP_BASE}/relay-info`, { signal: controller.signal });
    if (!res.ok) return null;
    const data = (await res.json()) as Partial<RelayInfo>;
    if (!data.peerId) return null;
    return {
      peerId: data.peerId,
      addr: withPeerSuffix(data.addr ?? DEFAULT_RELAY_ADDR, data.peerId),
      wsAddr: data.wsAddr ? withPeerSuffix(data.wsAddr, data.peerId) : undefined,
      httpBase: data.httpBase ?? SERVER_HTTP_BASE,
      chainWs: data.chainWs ?? CHAIN_WS_URL,
      startedAt: data.startedAt ?? Date.now(),
    };
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * 解析最终中继地址：env → /relay-info → 内置默认。
 * @param discover 是否允许发起网络发现（默认 true）
 */
export async function resolveRelayAddr(discover = true): Promise<RelayInfo> {
  if (ENV_RELAY_ADDR) {
    return {
      peerId: ENV_RELAY_PEER_ID,
      addr: withPeerSuffix(ENV_RELAY_ADDR, ENV_RELAY_PEER_ID),
      httpBase: SERVER_HTTP_BASE,
      chainWs: CHAIN_WS_URL,
      startedAt: Date.now(),
    };
  }
  if (discover) {
    const info = await fetchRelayInfo();
    if (info) return info;
  }
  return {
    peerId: ENV_RELAY_PEER_ID,
    addr: withPeerSuffix(DEFAULT_RELAY_ADDR, ENV_RELAY_PEER_ID),
    httpBase: SERVER_HTTP_BASE,
    chainWs: CHAIN_WS_URL,
    startedAt: Date.now(),
  };
}
